<template>
  <div class="client feedback-request-dialog">
    <!-- Top Floating Header -->
    <v-fade-transition name="v-fade-transition">
      <v-card
        v-show="showFloatingHeader"
        flat
        class="text-xs-center floating-profile-header"
        max-width="900"
      >
        <v-layout row align-start justify-space-around class="mt-2 mx-1">
          <v-flex xs4 class="text-xs-left">
            <v-layout
              row
              :wrap="$vuetify.breakpoint.smAndDown"
              v-if="application.status !== 'Withdrawn'"
            >
              <v-menu
                :close-on-content-click="false"
                max-width="400"
                max-height="50vh"
                offset-y
                bottom
                right
              >
                <v-btn
                  slot="activator"
                  class="v-btn--xs"
                  :color="themes.client.light.primaryActionButton"
                >
                  <v-icon small>mdi-folder</v-icon>
                </v-btn>
                <v-card>
                  <ApplicationDocuments v-if="!loading" :application="application" />
                </v-card>
              </v-menu>

              <v-menu
                v-if="application.questionnaires && application.questionnaires[0]"
                :close-on-content-click="false"
                max-width="400"
                max-height="50vh"
                offset-y
                bottom
              >
                <v-btn
                  class="v-btn--xs"
                  :color="themes.client.light.primaryActionButton"
                  slot="activator"
                >
                  <v-icon small>mdi-forum</v-icon>
                </v-btn>
                <v-card>
                  <ApplicationQuestionnaire :value="application.questionnaires" />
                </v-card>
              </v-menu>
              <v-btn
                v-if="!loading"
                :href="
                  `${$root.$api}/client/opportunities/${application.opportunity.id}/applications/${
                    application.id
                  }.pdf?auth_token=${$auth.getToken()}`
                "
                target="_blank"
                :color="themes.client.light.primaryActionButton"
                class="v-btn--xs"
              >
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
            </v-layout>
          </v-flex>

          <v-flex xs4 v-if="application && application.profile">
            <div class="text-xs-center" style="margin-top: -40px;">
              <v-avatar size="80">
                <img
                  v-if="application.profile && application.profile.avatar"
                  :src="application.profile.avatar"
                  v-img-orientation-changer
                />
                <img v-else src="/img/missing/avatars/original.png" />
              </v-avatar>
            </div>
            <div class="headline">
              <span v-if="application.profile.firstName">{{ application.profile.firstName }}</span>
              <span v-if="application.profile.lastName">{{ application.profile.lastName }}</span>
            </div>
          </v-flex>

          <v-flex xs4 v-if="application && application.profile">
            <v-layout row>
              <v-flex class="text-xs-right pa-0">
                <v-tooltip bottom>
                  <v-btn
                    icon
                    small
                    :color="themes.client.light.primaryActionButton"
                    slot="activator"
                    @click="copiedToast('email')"
                    v-clipboard:copy="application.profile.email"
                  >
                    <v-icon small>mdi-email</v-icon>
                  </v-btn>
                  <span>
                    {{ application.profile.email }}<br />
                    Click to copy
                  </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-btn
                    icon
                    small
                    :color="themes.client.light.primaryActionButton"
                    slot="activator"
                    @click="copiedToast('mobile')"
                    v-clipboard:copy="application.profile.mobileNumber"
                  >
                    <v-icon small>mdi-phone</v-icon>
                  </v-btn>
                  <span>
                    +{{ application.profile.mobileNumber }}<br />
                    Click to copy
                  </span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-menu offset-y left slot="activator">
                    <v-btn
                      slot="activator"
                      icon
                      small
                      :color="themes.client.light.primaryActionButton"
                    >
                      <v-icon small>mdi-information-variant</v-icon>
                    </v-btn>
                    <v-card>
                      <PersonalDetails
                        :profile="application.profile"
                        :actionItems="application.actionItems"
                      />
                    </v-card>
                  </v-menu>
                  <span> View Personal Information </span>
                </v-tooltip>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card>
    </v-fade-transition>

    <!-- Application Dialog -->
    <v-dialog
      :value="id > 0"
      @update:returnValue="$emit('close')"
      transition="scale-transition"
      max-width="900"
      width="70vw"
      lazy
    >
      <!-- Loading Indicator -->
      <v-card :class="{ 'pt-0': showFloatingHeader }">
        <div v-if="loading" key="feedback-request-loading-indicator">
          <div class="text-xs-center pa-3" v-if="application && application.profile">
            <v-progress-circular size="150" indeterminate :color="themes.client.light.primary">
              <v-avatar size="140">
                <img
                  v-if="application.profile.avatar" :src="application.profile.avatar"
                  v-img-orientation-changer
                />
                <img
                  v-else src="/img/missing/avatars/original.png"
                  v-img-orientation-changer
                />
              </v-avatar>
            </v-progress-circular>
            <div class="headline">
              <span v-if="application.profile.fullName">{{ application.profile.fullName }}</span>
            </div>
          </div>
        </div>

        <!-- Scrolloable Application Div -->
        <div
          v-else
          key="application-details"
          id="profile-container"
          v-scroll:#profile-container="onScroll"
          style="overflow-y: scroll; overflow-x: hidden; margin-bottom: 3rem;"
          :style="{ 'max-height': profileHeight }"
        >
        <Application :application="application"
          :show-application-rating="false"
          :show-application-notes="!!feedbackRequest.respondedAt"
        />
        </div>
        <!-- Bottom Toolbar -->
        <v-card-title class="pa-0" v-if="!loading && application.status !== 'Withdrawn'">
          <FeedbackToolbar
            v-show="feedbackRequest.respondedAt === null"
            :feedbackRequest="feedbackRequest"
            @done="$emit('close')"
            color="white"
          />
        </v-card-title>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Components
import Application from '@/components/client/applications/Application'
import ApplicationDocuments from '@/components/client/applications/ApplicationDocuments'
import ApplicationQuestionnaire from '@/components/client/applications/ApplicationQuestionnaire'
import FeedbackToolbar from '@/components/client/feedback_requests/FeedbackToolbar'
import PersonalDetails from '@/components/client/candidate/profile/PersonalDetails'

export default {
  components: {
    Application,
    ApplicationDocuments,
    ApplicationQuestionnaire,
    FeedbackToolbar,
    PersonalDetails,
  },

  props: { id: Number },

  created() {
    this.$store.dispatch('client/feedbackRequests/fetch', this.id).then(() => {
      this.loading = false
    })
  },

  data() {
    return {
      loading: true,
      showFloatingHeader: false,
      width: 750,
    }
  },

  computed: {
    feedbackRequest() {
      return this.$store.getters['client/feedbackRequests/find'](this.id)
    },

    application() {
      return this.feedbackRequest.opportunityApplication
    },

    profileHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return '80vh'
        case 'sm':
          return '80vh'
        default:
          return '82vh'
      }
    },
  },

  methods: {
    onScroll(e) {
      this.showFloatingHeader = e.target.scrollTop > 300
    },
    copiedToast(value) {
      this.$store.dispatch('snackbarShow', `Copied ${value}.`)
    },
  },
}
</script>

<style lang="scss">
div.client.feedback-request-dialog {
  .client.application-dialog nav div.v-toolbar__content {
    padding: 0;
  }

  .floating-profile-header {
    position: fixed;
    top: 6vh;
    width: 70vw;
    z-index: 10001;
    left: 50%;
    transform: translateX(-50%);
  }

  .floating-profile-header .v-btn--xs,
  .floating-profile-header .v-btn--xs::before {
    min-width: 0;
    width: 44px;
    height: 28px;
  }
}
</style>
